import type {
  FeeCategory,
  FeeItem,
} from '@/apis/extended/admin/interestRateApi';
import {
  CALCULATED_FEE_CATALOG,
  type CalculatedFeeCatalogItem,
  type CalculatedFeeKey,
} from '@/utils/fees/calculatedFeeCatalog';
import type { ProductEsitmation } from '@/types/ProductType';

export type CalculatedFeeRule = {
  key: CalculatedFeeKey;
  label: string;
  description?: string;
  category: FeeCategory;
  visible: boolean;
  includedInApr: boolean;
};

export type ComputedFeeLine = FeeItem & {
  computedAmount: number;
};

export type FeeBreakdown = {
  byCategory: Record<FeeCategory, ComputedFeeLine[]>;
  totalClosingCosts: number;
  totalAprFees: number;
};

export function computeFeeAmount(fee: FeeItem, loanAmount: number): number {
  const base =
    fee.amountType === 'percentOfLoan'
      ? (Number(fee.amount) / 100) * loanAmount
      : Number(fee.amount);
  const signed = fee.sign === 'subtract' ? -base : base;
  return Number.isFinite(signed) ? signed : 0;
}

export function computeFeeBreakdown(
  feeItems: FeeItem[] | undefined,
  loanAmount: number
): FeeBreakdown {
  const categories: FeeCategory[] = [
    'lender',
    'title',
    'government',
    'prepaid',
    'other',
  ];
  const byCategory: Record<FeeCategory, ComputedFeeLine[]> = {
    lender: [],
    title: [],
    government: [],
    prepaid: [],
    other: [],
  };

  const fees = Array.isArray(feeItems) ? feeItems : [];
  for (const fee of fees) {
    const category = categories.includes(fee.category) ? fee.category : 'other';
    const computedAmount = computeFeeAmount(fee, loanAmount);
    byCategory[category].push({ ...fee, computedAmount });
  }

  const all = categories.flatMap((c) => byCategory[c]);
  const totalClosingCosts = all.reduce((sum, f) => sum + f.computedAmount, 0);
  const totalAprFees = all
    .filter((f) => !!f.includedInApr)
    .reduce((sum, f) => sum + f.computedAmount, 0);

  return {
    byCategory,
    totalClosingCosts,
    totalAprFees,
  };
}

export function computeMergedFeeBreakdown(params: {
  estimation: ProductEsitmation;
  calculatedFeeRules?: CalculatedFeeRule[];
  feeItems?: FeeItem[];
  loanAmount: number;
}): FeeBreakdown {
  const { estimation, calculatedFeeRules, feeItems, loanAmount } = params;
  const categories: FeeCategory[] = [
    'lender',
    'title',
    'government',
    'prepaid',
    'other',
  ];

  // Build rule map (admin overrides)
  const ruleMap = new Map<string, CalculatedFeeRule>();
  (calculatedFeeRules || []).forEach((r) => {
    ruleMap.set(r.key, r);
  });

  // Convert calculated fees -> FeeItem-like lines so we can reuse the breakdown type
  const calculatedAsFeeItems: FeeItem[] = [];
  for (const item of CALCULATED_FEE_CATALOG as CalculatedFeeCatalogItem[]) {
    if (item.isApplicable && !item.isApplicable(estimation)) continue;
    const amount = item.compute(estimation);
    // Skip truly empty fees unless admin explicitly keeps visible
    const rule = ruleMap.get(item.key);
    const visible = rule ? !!rule.visible : item.defaultVisible;
    const includedInApr = rule
      ? !!rule.includedInApr
      : item.defaultIncludedInApr;
    const category = rule?.category || item.category;
    const label = rule?.label || item.label;
    const description = rule?.description || item.defaultDescription;

    if (!visible) continue;
    if (amount === 0 && !rule) continue;

    calculatedAsFeeItems.push({
      key: `calc_${item.key}`,
      label,
      description,
      category,
      sign: 'add',
      amountType: 'flat',
      amount,
      includedInApr,
    });
  }

  // Combine calculated fees + admin adjustments (feeItems)
  const combined = [...calculatedAsFeeItems, ...(feeItems || [])];

  // Compute breakdown (reuses existing logic)
  const byCategory: Record<FeeCategory, ComputedFeeLine[]> = {
    lender: [],
    title: [],
    government: [],
    prepaid: [],
    other: [],
  };

  for (const fee of combined) {
    const category = categories.includes(fee.category) ? fee.category : 'other';
    const computedAmount =
      fee.key.startsWith('calc_') && fee.amountType === 'flat'
        ? Number(fee.amount) || 0
        : computeFeeAmount(fee, loanAmount);
    byCategory[category].push({ ...fee, computedAmount });
  }

  const all = categories.flatMap((c) => byCategory[c]);
  const totalClosingCosts = all.reduce((sum, f) => sum + f.computedAmount, 0);
  const totalAprFees = all
    .filter((f) => !!f.includedInApr)
    .reduce((sum, f) => sum + f.computedAmount, 0);

  return { byCategory, totalClosingCosts, totalAprFees };
}
